import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { getPriceData } from '../utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SearchAutocomplete = ({ onClose }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const inputRef = useRef(null);
    const navigate = useNavigate();

    const popularSearches = ['Баклава', 'Локум', 'Кафе', 'Зехтин', 'Суджук'];

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    useEffect(() => {
        const searchProducts = async () => {
            if (query.length < 2) {
                setResults([]);
                return;
            }

            setLoading(true);
            try {
                const response = await axios.get(`${API}/products`, {
                    params: { search: query, limit: 6 }
                });
                setResults(response.data.products || []);
            } catch (error) {
                console.error('Search error:', error);
                setResults([]);
            }
            setLoading(false);
        };

        const debounce = setTimeout(searchProducts, 300);
        return () => clearTimeout(debounce);
    }, [query]);

    const handleSearch = (e) => {
        e.preventDefault();
        if (query.trim()) {
            navigate(`/products?search=${encodeURIComponent(query)}`);
            onClose?.();
        }
    };

    const handleProductClick = (productId) => {
        navigate(`/product/${productId}`);
        onClose?.();
    };

    const handlePopularClick = (term) => {
        setQuery(term);
        navigate(`/products?search=${encodeURIComponent(term)}`);
        onClose?.();
    };

    return (
        <div className="relative w-full">
            <form onSubmit={handleSearch}>
                <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-amber-400" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setIsOpen(true);
                        }}
                        onFocus={() => setIsOpen(true)}
                        placeholder="Търсене на продукти..."
                        className="w-full pl-12 pr-10 py-3 bg-white border-2 border-amber-200 rounded-xl focus:border-amber-400 focus:ring-2 focus:ring-amber-200 outline-none transition-all text-slate-800"
                        data-testid="search-autocomplete-input"
                    />
                    {query && (
                        <button
                            type="button"
                            onClick={() => setQuery('')}
                            className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-amber-100 rounded-full transition-colors"
                        >
                            <X className="h-4 w-4 text-slate-400" />
                        </button>
                    )}
                </div>
            </form>

            {/* Dropdown */}
            {isOpen && (query.length >= 2 || results.length > 0 || query.length === 0) && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-amber-100 overflow-hidden z-50">
                    {/* Loading */}
                    {loading && (
                        <div className="p-4 text-center text-slate-500">
                            <div className="animate-spin w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full mx-auto mb-2" />
                            Търсене...
                        </div>
                    )}

                    {/* Results */}
                    {!loading && results.length > 0 && (
                        <div className="py-2">
                            {results.map((product) => (
                                <button
                                    key={product.product_id}
                                    onClick={() => handleProductClick(product.product_id)}
                                    className="w-full flex items-center gap-4 px-4 py-3 hover:bg-amber-50 transition-colors text-left"
                                >
                                    <img 
                                        src={product.image_url} 
                                        alt={product.name}
                                        className="w-14 h-14 object-cover rounded-lg"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-slate-800 truncate">{product.name}</p>
                                        <p className="text-amber-600 font-bold">{getPriceData(product.price).eurFormatted} €</p>
                                    </div>
                                </button>
                            ))}
                            
                            {/* View all results */}
                            <button
                                onClick={handleSearch}
                                className="w-full px-4 py-3 text-center text-amber-600 hover:bg-amber-50 font-medium border-t border-amber-100"
                            >
                                Виж всички резултати за "{query}"
                            </button>
                        </div>
                    )}

                    {/* No results */}
                    {!loading && query.length >= 2 && results.length === 0 && (
                        <div className="p-6 text-center text-slate-500">
                            <p>Няма намерени продукти за "{query}"</p>
                        </div>
                    )}

                    {/* Popular searches (when empty) */}
                    {!loading && query.length === 0 && (
                        <div className="p-4">
                            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                                <TrendingUp className="h-4 w-4" />
                                Популярни търсения
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {popularSearches.map((term) => (
                                    <button
                                        key={term}
                                        onClick={() => handlePopularClick(term)}
                                        className="px-4 py-2 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-full text-sm font-medium transition-colors"
                                    >
                                        {term}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchAutocomplete;
